import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut, Pie } from 'react-chartjs-2';
import { 
  BarChart, 
  TrendingUp, 
  Users, 
  Award, 
  Download, 
  Calendar,
  Target,
  ThumbsUp,
  ThumbsDown, 
  Minus,
  AlertCircle,
  CheckCircle,
  Clock,
  LogOut,
  Shield
} from "lucide-react";
import axios from "axios";
import { format } from 'date-fns';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ onLogout, username }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [npsDetails, setNpsDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('last_30_days');
  const [selectedTrendPeriod, setSelectedTrendPeriod] = useState('weekly');
  const [activeTab, setActiveTab] = useState('overview');

  // Setup axios interceptor for auth
  useEffect(() => {
    const token = localStorage.getItem('admin_session_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Setup response interceptor to handle auth errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (err) {
      // Ignore logout errors
    } finally {
      // Clear local storage
      localStorage.removeItem('admin_session_token');
      localStorage.removeItem('admin_username');
      localStorage.removeItem('admin_expires_at');
      
      // Clear axios header
      delete axios.defaults.headers.common['Authorization'];
      
      onLogout();
    }
  };

  useEffect(() => {
    loadDashboardData();
    loadTrendsData();
    loadNpsDetails();
  }, [selectedPeriod, selectedTrendPeriod]);

  const loadDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats?time_period=${selectedPeriod}`);
      setDashboardData(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Dashboard-Daten');
      console.error(err);
    }
  };

  const loadTrendsData = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/trends?period=${selectedTrendPeriod}`);
      setTrendsData(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Trend-Daten');
      console.error(err);
    }
  };

  const loadNpsDetails = async () => {
    try {
      const response = await axios.get(`${API}/admin/nps/details`);
      setNpsDetails(response.data);
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Laden der NPS-Details');
      console.error(err);
      setLoading(false);
    }
  };

  const exportData = async (format = 'csv') => {
    try {
      setLoading(true);
      
      // Check if user is authenticated
      const token = localStorage.getItem('admin_session_token');
      if (!token) {
        alert('❌ Sie sind nicht angemeldet. Bitte loggen Sie sich erneut ein.');
        return;
      }

      // Show loading message
      const loadingAlert = document.createElement('div');
      loadingAlert.innerHTML = '⏳ CSV-Export wird vorbereitet...';
      loadingAlert.style.cssText = `
        position: fixed; top: 20px; right: 20px; 
        background: #3b82f6; color: white; padding: 1rem; 
        border-radius: 8px; z-index: 10001; box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      `;
      document.body.appendChild(loadingAlert);

      try {
        // Method 1: Try fetch with proper headers
        const response = await fetch(`${API}/admin/export/csv?format=${format}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/csv,application/csv,*/*'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Get the filename from headers
        const contentDisposition = response.headers.get('content-disposition');
        const filenameMatch = contentDisposition && contentDisposition.match(/filename="?([^"]*)"?/);
        const filename = filenameMatch ? filenameMatch[1] : `eurosill_bestandskundenumfrage_${new Date().toISOString().split('T')[0]}.csv`;

        // Get the data as blob
        const blob = await response.blob();
        
        // Create download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        
        // Update loading message to success
        loadingAlert.innerHTML = `✅ CSV-Export erfolgreich! Datei: ${filename}`;
        loadingAlert.style.background = '#22c55e';
        
        setTimeout(() => {
          document.body.removeChild(loadingAlert);
        }, 3000);

      } catch (fetchError) {
        console.error('Fetch method failed:', fetchError);
        
        // Method 2: Fallback - Open in new window
        const exportUrl = `${API}/admin/export/csv?format=${format}&token=${encodeURIComponent(token)}`;
        
        loadingAlert.innerHTML = '🔄 Alternativer Download wird gestartet...';
        loadingAlert.style.background = '#eab308';
        
        setTimeout(() => {
          const newWindow = window.open(exportUrl, '_blank');
          
          setTimeout(() => {
            if (newWindow) {
              newWindow.close();
            }
            loadingAlert.innerHTML = '✅ CSV-Export gestartet! Prüfen Sie Ihre Downloads.';
            loadingAlert.style.background = '#22c55e';
            
            setTimeout(() => {
              document.body.removeChild(loadingAlert);
            }, 3000);
          }, 2000);
        }, 1000);
      }
      
    } catch (err) {
      console.error('Export error:', err);
      
      // Show error message
      alert(`❌ CSV-Export Fehler:\n\n${err.message}\n\nFalls das Problem weiterhin besteht, wenden Sie sich an den Administrator.`);
      
    } finally {
      setLoading(false);
    }
  };

  const directExport = () => {
    const token = localStorage.getItem('admin_session_token');
    if (!token) {
      alert('❌ Sie sind nicht angemeldet. Bitte loggen Sie sich erneut ein.');
      return;
    }

    // Create a direct link with token
    const exportUrl = `${API}/admin/export/csv-direct?token=${encodeURIComponent(token)}`;
    
    // Show immediate feedback
    const feedback = document.createElement('div');
    feedback.innerHTML = '🔄 CSV-Export wird gestartet...';
    feedback.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 10001;
      background: #3b82f6; color: white; padding: 1rem; border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    document.body.appendChild(feedback);
    
    // Open in new window/tab
    const newWindow = window.open(exportUrl, '_blank');
    
    setTimeout(() => {
      feedback.innerHTML = '✅ CSV-Download gestartet!';
      feedback.style.background = '#22c55e';
      
      setTimeout(() => {
        if (feedback.parentNode) {
          document.body.removeChild(feedback);
        }
      }, 2000);
      
      // Close the popup window if it opened
      if (newWindow && !newWindow.closed) {
        setTimeout(() => {
          try {
            newWindow.close();
          } catch (e) {
            // Ignore errors when closing popup
          }
        }, 3000);
      }
    }, 1000);
  };

  const exportExcel = async () => {
    try {
      setLoading(true);
      
      // Check if user is authenticated
      const token = localStorage.getItem('admin_session_token');
      if (!token) {
        alert('❌ Sie sind nicht angemeldet. Bitte loggen Sie sich erneut ein.');
        return;
      }

      // Show loading message
      const loadingAlert = document.createElement('div');
      loadingAlert.innerHTML = '📊 Excel-Analyse wird erstellt...';
      loadingAlert.style.cssText = `
        position: fixed; top: 20px; right: 20px; 
        background: #16a34a; color: white; padding: 1rem; 
        border-radius: 8px; z-index: 10001; box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      `;
      document.body.appendChild(loadingAlert);

      try {
        // Fetch Excel analysis
        const response = await fetch(`${API}/admin/export/excel`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,*/*'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Get the filename from headers
        const contentDisposition = response.headers.get('content-disposition');
        const filenameMatch = contentDisposition && contentDisposition.match(/filename="?([^"]*)"?/);
        const filename = filenameMatch ? filenameMatch[1] : `eurosill_umfrage_analyse_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Get the data as blob
        const blob = await response.blob();
        
        // Create download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        
        // Update loading message to success
        loadingAlert.innerHTML = `✅ Excel-Analyse erstellt! 📊<br/>📁 Datei: ${filename}<br/>🎯 Mit NPS-Kategorisierung und Diagrammen`;
        loadingAlert.style.background = '#22c55e';
        loadingAlert.style.whiteSpace = 'nowrap';
        
        setTimeout(() => {
          document.body.removeChild(loadingAlert);
        }, 4000);

      } catch (fetchError) {
        console.error('Excel export failed:', fetchError);
        loadingAlert.innerHTML = '❌ Excel-Export fehlgeschlagen!';
        loadingAlert.style.background = '#dc2626';
        
        setTimeout(() => {
          document.body.removeChild(loadingAlert);
        }, 3000);
        
        throw fetchError;
      }
      
    } catch (err) {
      console.error('Excel export error:', err);
      alert(`❌ Excel-Export Fehler:\n\n${err.message}\n\nBitte versuchen Sie es erneut.`);
    } finally {
      setLoading(false);
    }
  };

  const cleanupTestData = async () => {
    if (!window.confirm('Sind Sie sicher, dass Sie alle Test-Daten löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.delete(`${API}/admin/cleanup/test-data`);
      
      if (response.data.success) {
        alert(`✅ Test-Daten bereinigt!\n\nGelöscht: ${response.data.deleted_count} Test-Einträge\nVerbleibend: ${response.data.total_after} echte Kundeneinträge`);
        
        // Reload all data
        await loadDashboardData();
        await loadTrendsData();
        await loadNpsDetails();
      }
    } catch (err) {
      setError('Fehler beim Bereinigen der Test-Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetAllData = async () => {
    // Double confirmation for complete data reset
    const firstConfirm = window.confirm(
      '🚨 ACHTUNG: TAG ZERO RESET\n\n' +
      'Sie sind dabei, ALLE Umfragedaten unwiderruflich zu löschen!\n\n' +
      '⚠️  Dies löscht:\n' +
      '   • Alle Kundenumfragen\n' +
      '   • Alle Bewertungen\n' +
      '   • Alle Kontaktdaten\n' +
      '   • Alle NPS-Daten\n\n' +
      'Diese Aktion kann NICHT rückgängig gemacht werden!\n\n' +
      'Möchten Sie wirklich fortfahren?'
    );
    
    if (!firstConfirm) {
      return;
    }
    
    const secondConfirm = window.confirm(
      '🔥 LETZTE WARNUNG - TAG ZERO RESET\n\n' +
      'Sie sind dabei, die komplette Datenbank zu leeren!\n\n' +
      '💀 ALLE DATEN GEHEN UNWIDERRUFLICH VERLOREN!\n\n' +
      'Tippen Sie "TAG ZERO" um zu bestätigen:'
    );
    
    if (!secondConfirm) {
      return;
    }
    
    const textConfirm = prompt(
      '🚀 Finale Bestätigung für TAG ZERO RESET:\n\n' +
      'Tippen Sie exakt "TAG ZERO" (ohne Anführungszeichen) um fortzufahren:'
    );
    
    if (textConfirm !== 'TAG ZERO') {
      alert('❌ Reset abgebrochen - Eingabe war nicht korrekt');
      return;
    }
    
    try {
      setLoading(true);
      
      // Show dramatic loading message
      const resetAlert = document.createElement('div');
      resetAlert.innerHTML = '🚀 TAG ZERO RESET läuft...';
      resetAlert.style.cssText = `
        position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
        background: #dc2626; color: white; padding: 2rem; border-radius: 16px;
        z-index: 10001; box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        font-size: 1.5rem; font-weight: bold; text-align: center;
        min-width: 300px;
      `;
      document.body.appendChild(resetAlert);
      
      const response = await axios.delete(`${API}/admin/reset/all-data`);
      
      if (response.data.success) {
        resetAlert.innerHTML = `
          🎉 TAG ZERO RESET ERFOLGREICH!<br/><br/>
          🗑️ ${response.data.deleted_count} Datensätze gelöscht<br/>
          📅 Reset-Datum: ${new Date().toLocaleDateString('de-DE')}<br/>
          🚀 Bereit für Produktionsstart!
        `;
        resetAlert.style.background = '#22c55e';
        
        setTimeout(() => {
          document.body.removeChild(resetAlert);
          
          // Reload all dashboard data
          loadDashboardData();
          loadTrendsData();
          loadNpsDetails();
          
          alert(`✅ TAG ZERO RESET abgeschlossen!\n\n📊 Dashboard wird mit leeren Daten neu geladen.\n🚀 Das System ist bereit für echte Produktionsdaten!`);
        }, 4000);
      }
    } catch (err) {
      console.error('Reset error:', err);
      alert(`❌ Reset-Fehler: ${err.response?.data?.detail || err.message}`);
      setError('Fehler beim Daten-Reset');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Dashboard wird geladen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard error">
        <AlertCircle size={48} />
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Neu laden</button>
      </div>
    );
  }

  // Chart configurations
  const satisfactionChartData = dashboardData?.distributions?.satisfaction ? {
    labels: ['1 Stern', '2 Sterne', '3 Sterne', '4 Sterne', '5 Sterne'],
    datasets: [{
      label: 'Anzahl Bewertungen',
      data: [
        dashboardData.distributions.satisfaction['1'] || 0,
        dashboardData.distributions.satisfaction['2'] || 0,
        dashboardData.distributions.satisfaction['3'] || 0,
        dashboardData.distributions.satisfaction['4'] || 0,
        dashboardData.distributions.satisfaction['5'] || 0
      ],
      backgroundColor: [
        '#ef4444', '#f97316', '#eab308', '#22c55e', '#16a34a'
      ],
      borderColor: [
        '#dc2626', '#ea580c', '#ca8a04', '#15803d', '#14532d'
      ],
      borderWidth: 1
    }]
  } : null;

  const npsChartData = dashboardData?.nps_analysis ? {
    labels: ['Detractors (0-6)', 'Passives (7-8)', 'Promoters (9-10)'],
    datasets: [{
      data: [
        dashboardData.nps_analysis.detractors,
        dashboardData.nps_analysis.passives,
        dashboardData.nps_analysis.promoters
      ],
      backgroundColor: ['#ef4444', '#eab308', '#22c55e'],
      borderColor: ['#dc2626', '#ca8a04', '#15803d'],
      borderWidth: 2
    }]
  } : null;

  const averagesChartData = dashboardData?.averages ? {
    labels: [
      'Gesamtzufriedenheit',
      'Produktqualität',
      'Preis-Leistung', 
      'Lieferzuverlässigkeit',
      'Liefervollständigkeit',
      'Beratungskompetenz',
      'Digitale Kommunikation'
    ],
    datasets: [{
      label: 'Durchschnittsbewertung',
      data: [
        dashboardData.averages.overall_satisfaction,
        dashboardData.averages.product_quality_comparison,
        dashboardData.averages.price_performance,
        dashboardData.averages.delivery_reliability,
        dashboardData.averages.delivery_completeness,
        dashboardData.averages.consultation_competence,
        dashboardData.averages.digital_communication
      ],
      backgroundColor: 'rgba(231, 91, 18, 0.6)',
      borderColor: '#E75B12',
      borderWidth: 2
    }]
  } : null;

  const trendsChartData = trendsData?.data ? {
    labels: trendsData.data.map(item => item.period),
    datasets: [
      {
        label: 'Anzahl Antworten',
        data: trendsData.data.map(item => item.responses),
        borderColor: '#E75B12',
        backgroundColor: 'rgba(231, 91, 18, 0.1)',
        yAxisID: 'y'
      },
      {
        label: 'Ø Zufriedenheit',
        data: trendsData.data.map(item => item.avg_satisfaction),
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        yAxisID: 'y1'
      },
      {
        label: 'Ø NPS',
        data: trendsData.data.map(item => item.avg_nps),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        yAxisID: 'y1'
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      },
      title: {
        display: false
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        max: 10,
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  const getNpsStatusIcon = (score) => {
    if (score >= 50) return <CheckCircle className="nps-excellent" />;
    if (score >= 0) return <Clock className="nps-good" />;
    return <AlertCircle className="nps-poor" />;
  };

  const getNpsStatusText = (score) => {
    if (score >= 50) return "Exzellent";
    if (score >= 0) return "Gut";
    return "Verbesserungsbedarf";
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="company-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_neukunden-survey/artifacts/jcwk0oru_1_Logo_Eurosill_klein.jpg" 
              alt="Eurosill Logo" 
              className="logo-image"
            />
          </div>
          <div>
            <h1>Admin Dashboard - Bestandskundenumfrage</h1>
            <p>Umfassende Analyse und Auswertung der Kundenfeedbacks</p>
          </div>
          <div className="admin-info">
            <div className="admin-badge">
              <Shield size={16} />
              <span>Angemeldet als: {username}</span>
            </div>
          </div>
        </div>
        
        <div className="header-controls">
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="period-selector"
          >
            <option value="all">Alle Daten</option>
            <option value="last_30_days">Letzte 30 Tage</option>
            <option value="last_90_days">Letzte 90 Tage</option>
            <option value="last_year">Letztes Jahr</option>
          </select>
          
          <button onClick={cleanupTestData} className="cleanup-btn">
            🧹 Test-Daten bereinigen
          </button>
          
          <button onClick={resetAllData} className="reset-all-btn">
            🚀 TAG ZERO RESET
          </button>
          
          <button onClick={resetAllData} className="reset-all-btn">
            🚀 TAG ZERO RESET
          </button>
          
          <button onClick={() => exportData('csv')} className="export-btn">
            <Download size={16} />
            CSV Export (Auto)
          </button>
          
          <button onClick={directExport} className="export-btn-direct">
            📋 CSV Export (Direkt)
          </button>
          
          <button onClick={exportExcel} className="export-excel-btn">
            📊 Excel-Analyse
          </button>
          
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={16} />
            Abmelden
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart size={20} />
          Übersicht
        </button>
        <button 
          className={`tab ${activeTab === 'nps' ? 'active' : ''}`}
          onClick={() => setActiveTab('nps')}
        >
          <Target size={20} />
          NPS Analyse
        </button>
        <button 
          className={`tab ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          <TrendingUp size={20} />
          Trends
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Key Metrics */}
          <div className="metrics-grid">
            <div className="metric-card">
              <Users className="metric-icon" />
              <div className="metric-content">
                <h3>Gesamte Antworten</h3>
                <p className="metric-value">{dashboardData?.total_responses || 0}</p>
              </div>
            </div>

            <div className="metric-card">
              <Award className="metric-icon satisfaction" />
              <div className="metric-content">
                <h3>Ø Zufriedenheit</h3>
                <p className="metric-value">{dashboardData?.averages?.overall_satisfaction || 0}/5</p>
                <div className="metric-stars">
                  {'⭐'.repeat(Math.round(dashboardData?.averages?.overall_satisfaction || 0))}
                </div>
              </div>
            </div>

            <div className="metric-card">
              <Target className="metric-icon nps" />
              <div className="metric-content">
                <h3>NPS Score</h3>
                <p className="metric-value">{dashboardData?.nps_analysis?.score || 0}</p>
                <div className="nps-status">
                  {getNpsStatusIcon(dashboardData?.nps_analysis?.score || 0)}
                  <span>{getNpsStatusText(dashboardData?.nps_analysis?.score || 0)}</span>
                </div>
              </div>
            </div>

            <div className="metric-card">
              <CheckCircle className="metric-icon quality" />
              <div className="metric-content">
                <h3>Ø Produktqualität</h3>
                <p className="metric-value">{dashboardData?.averages?.product_quality_comparison || 0}/5</p>
                <span className="metric-subtitle">vs. Konkurrenz</span>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            <div className="chart-card">
              <h3>Zufriedenheitsverteilung</h3>
              {satisfactionChartData && (
                <Bar data={satisfactionChartData} options={{responsive: true}} />
              )}
            </div>

            <div className="chart-card">
              <h3>Durchschnittsbewertungen</h3>
              {averagesChartData && (
                <Bar data={averagesChartData} options={{
                  responsive: true,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 5
                    }
                  }
                }} />
              )}
            </div>

            <div className="chart-card">
              <h3>Sortiment Bedürfnisse</h3>
              {dashboardData?.distributions?.product_range && (
                <Doughnut data={{
                  labels: Object.keys(dashboardData.distributions.product_range),
                  datasets: [{
                    data: Object.values(dashboardData.distributions.product_range),
                    backgroundColor: ['#22c55e', '#eab308', '#ef4444'],
                    borderColor: ['#15803d', '#ca8a04', '#dc2626'],
                    borderWidth: 2
                  }]
                }} />
              )}
            </div>

            <div className="chart-card">
              <h3>Liefer- & Zahlungskonditionen</h3>
              {dashboardData?.distributions?.delivery_conditions && (
                <Doughnut data={{
                  labels: Object.keys(dashboardData.distributions.delivery_conditions),
                  datasets: [{
                    data: Object.values(dashboardData.distributions.delivery_conditions),
                    backgroundColor: ['#22c55e', '#eab308', '#ef4444'],
                    borderColor: ['#15803d', '#ca8a04', '#dc2626'],
                    borderWidth: 2
                  }]
                }} />
              )}
            </div>
          </div>
        </>
      )}

      {/* NPS Analysis Tab */}
      {activeTab === 'nps' && (
        <div className="nps-analysis">
          <div className="nps-overview">
            <div className="nps-score-card">
              <h2>Net Promoter Score</h2>
              <div className="nps-score-display">
                <span className="nps-score-number">{dashboardData?.nps_analysis?.score || 0}</span>
                <div className="nps-score-status">
                  {getNpsStatusIcon(dashboardData?.nps_analysis?.score || 0)}
                  <span>{getNpsStatusText(dashboardData?.nps_analysis?.score || 0)}</span>
                </div>
              </div>
            </div>

            <div className="nps-breakdown">
              <div className="nps-category promoters">
                <ThumbsUp size={24} />
                <h3>Promoters (9-10)</h3>
                <p className="category-count">{dashboardData?.nps_analysis?.promoters || 0}</p>
                <p className="category-percent">{dashboardData?.nps_analysis?.promoters_percent || 0}%</p>
              </div>

              <div className="nps-category passives">
                <Minus size={24} />
                <h3>Passives (7-8)</h3>
                <p className="category-count">{dashboardData?.nps_analysis?.passives || 0}</p>
                <p className="category-percent">{dashboardData?.nps_analysis?.passives_percent || 0}%</p>
              </div>

              <div className="nps-category detractors">
                <ThumbsDown size={24} />
                <h3>Detractors (0-6)</h3>
                <p className="category-count">{dashboardData?.nps_analysis?.detractors || 0}</p>
                <p className="category-percent">{dashboardData?.nps_analysis?.detractors_percent || 0}%</p>
              </div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-card nps-chart">
              <h3>NPS Verteilung</h3>
              {npsChartData && <Pie data={npsChartData} />}
            </div>
          </div>

          {/* NPS Details for Follow-up */}
          {npsDetails && (
            <div className="nps-details">
              <h3>Detaillierte NPS-Analyse für Nacharbeit</h3>
              
              <div className="nps-tables">
                <div className="nps-table-section">
                  <h4 className="detractors-header">
                    <ThumbsDown size={20} />
                    Detractors - Nacharbeit erforderlich ({npsDetails.detractors?.length || 0})
                  </h4>
                  <div className="table-container">
                    <table className="nps-table">
                      <thead>
                        <tr>
                          <th>Kunde</th>
                          <th>Unternehmen</th>
                          <th>E-Mail</th>
                          <th>NPS</th>
                          <th>Zufriedenheit</th>
                          <th>Verbesserungsvorschläge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {npsDetails.detractors?.map((customer, index) => (
                          <tr key={index} className="detractor-row">
                            <td>{customer.name}</td>
                            <td>{customer.company || '-'}</td>
                            <td>{customer.email}</td>
                            <td className="nps-score-cell">{customer.nps_score}/10</td>
                            <td>{customer.satisfaction}/5</td>
                            <td className="suggestions-cell">{customer.improvement_suggestions || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="nps-table-section">
                  <h4 className="promoters-header">
                    <ThumbsUp size={20} />
                    Promoters - Potentielle Referenzen ({npsDetails.promoters?.length || 0})
                  </h4>
                  <div className="table-container">
                    <table className="nps-table">
                      <thead>
                        <tr>
                          <th>Kunde</th>
                          <th>Unternehmen</th>
                          <th>E-Mail</th>
                          <th>NPS</th>
                          <th>Was beibehalten</th>
                        </tr>
                      </thead>
                      <tbody>
                        {npsDetails.promoters?.map((customer, index) => (
                          <tr key={index} className="promoter-row">
                            <td>{customer.name}</td>
                            <td>{customer.company || '-'}</td>
                            <td>{customer.email}</td>
                            <td className="nps-score-cell">{customer.nps_score}/10</td>
                            <td className="suggestions-cell">{customer.keep_suggestions || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && (
        <div className="trends-analysis">
          <div className="trends-controls">
            <h2>Zeitbasierte Entwicklung</h2>
            <select 
              value={selectedTrendPeriod} 
              onChange={(e) => setSelectedTrendPeriod(e.target.value)}
              className="period-selector"
            >
              <option value="daily">Täglich (letzte 30 Tage)</option>
              <option value="weekly">Wöchentlich (letzte 3 Monate)</option>
              <option value="monthly">Monatlich (letztes Jahr)</option>
            </select>
          </div>

          <div className="chart-card large">
            <h3>Entwicklung über Zeit</h3>
            {trendsChartData && (
              <Line data={trendsChartData} options={chartOptions} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;